export interface Reaction {
  id: string;
  reactants: string[];
  products: string[];
  equation: string;
  type: "synthesis" | "decomposition" | "single-replacement" | "double-replacement" | "combustion" | "acid-base";
  typeLabel: string;
  description: string;
  observation: string;
  energy: "exothermic" | "endothermic";
  color?: string; // CSS color for visual effect in beaker
  /** If true, the order chemicals are added matters (e.g., always add acid to water). */
  orderMatters?: boolean;
  /** Hint shown when orderMatters is true. */
  orderHint?: string;
  /** Minimum temperature in °C required to trigger this reaction. Defaults to 25 (room temp). */
  minTemperature?: number;
}

export const reactions: Reaction[] = [
  {
    id: "na-cl",
    reactants: ["Na", "Cl₂"],
    products: ["NaCl"],
    equation: "2Na + Cl₂ → 2NaCl",
    type: "synthesis",
    typeLabel: "Synthesis",
    description: "Sodium reacts vigorously with chlorine gas to form sodium chloride (table salt).",
    observation: "Bright yellow flame, white crystalline solid forms",
    energy: "exothermic",
    color: "hsl(45, 90%, 70%)",
  },
  {
    id: "h-o",
    reactants: ["H₂", "O₂"],
    products: ["H₂O"],
    equation: "2H₂ + O₂ → 2H₂O",
    type: "synthesis",
    typeLabel: "Synthesis",
    description: "Hydrogen gas combusts with oxygen to form water.",
    observation: "Explosive pop, steam/water vapor produced",
    energy: "exothermic",
    color: "hsl(200, 60%, 70%)",
  },
  {
    id: "fe-o",
    reactants: ["Fe", "O₂"],
    products: ["Fe₂O₃"],
    equation: "4Fe + 3O₂ → 2Fe₂O₃",
    type: "synthesis",
    typeLabel: "Synthesis",
    description: "Iron reacts with oxygen to form iron(III) oxide (rust).",
    observation: "Reddish-brown solid forms, sparks if heated",
    energy: "exothermic",
    color: "hsl(15, 70%, 45%)",
  },
  {
    id: "mg-o",
    reactants: ["Mg", "O₂"],
    products: ["MgO"],
    equation: "2Mg + O₂ → 2MgO",
    type: "synthesis",
    typeLabel: "Synthesis",
    description: "Magnesium burns brilliantly in oxygen to form magnesium oxide.",
    observation: "Blinding white light, white powder residue",
    energy: "exothermic",
    color: "hsl(0, 0%, 95%)",
  },
  {
    id: "na-h2o",
    reactants: ["Na", "H₂O"],
    products: ["NaOH", "H₂"],
    equation: "2Na + 2H₂O → 2NaOH + H₂",
    type: "single-replacement",
    typeLabel: "Single Replacement",
    description: "Sodium reacts violently with water, producing sodium hydroxide and hydrogen gas.",
    observation: "Fizzing, heat, may ignite - purple/yellow flame",
    energy: "exothermic",
    color: "hsl(270, 60%, 60%)",
    orderMatters: true,
    orderHint: "Always add the metal to water - never water to molten metal.",
  },
  {
    id: "hcl-naoh",
    reactants: ["HCl", "NaOH"],
    products: ["NaCl", "H₂O"],
    equation: "HCl + NaOH → NaCl + H₂O",
    type: "acid-base",
    typeLabel: "Acid-Base Neutralization",
    description: "Hydrochloric acid neutralizes sodium hydroxide to form salt and water.",
    observation: "Solution warms, pH becomes neutral",
    energy: "exothermic",
    color: "hsl(120, 30%, 60%)",
    orderMatters: true,
    orderHint: "Always add acid to base (or to water) - never the reverse, to avoid violent splashing.",
  },
  {
    id: "zn-hcl",
    reactants: ["Zn", "HCl"],
    products: ["ZnCl₂", "H₂"],
    equation: "Zn + 2HCl → ZnCl₂ + H₂",
    type: "single-replacement",
    typeLabel: "Single Replacement",
    description: "Zinc dissolves in hydrochloric acid, releasing hydrogen gas.",
    observation: "Bubbling/effervescence, zinc dissolves",
    energy: "exothermic",
    color: "hsl(180, 20%, 70%)",
  },
  {
    id: "caco3-heat",
    reactants: ["CaCO₃"],
    products: ["CaO", "CO₂"],
    equation: "CaCO₃ → CaO + CO₂",
    type: "decomposition",
    typeLabel: "Decomposition (heating)",
    description: "Calcium carbonate decomposes upon heating into calcium oxide and carbon dioxide.",
    observation: "White powder remains, gas released",
    energy: "endothermic",
    color: "hsl(0, 0%, 85%)",
    minTemperature: 825,
  },
  {
    id: "agno3-nacl",
    reactants: ["AgNO₃", "NaCl"],
    products: ["AgCl", "NaNO₃"],
    equation: "AgNO₃ + NaCl → AgCl↓ + NaNO₃",
    type: "double-replacement",
    typeLabel: "Double Replacement (Precipitation)",
    description: "Silver nitrate reacts with sodium chloride to form a white precipitate of silver chloride.",
    observation: "White curdy precipitate forms immediately",
    energy: "exothermic",
    color: "hsl(0, 0%, 90%)",
  },
  {
    id: "cu-agno3",
    reactants: ["Cu", "AgNO₃"],
    products: ["Cu(NO₃)₂", "Ag"],
    equation: "Cu + 2AgNO₃ → Cu(NO₃)₂ + 2Ag",
    type: "single-replacement",
    typeLabel: "Single Replacement",
    description: "Copper displaces silver from silver nitrate solution. Silver crystals deposit on copper.",
    observation: "Solution turns blue, silver crystals grow on copper",
    energy: "exothermic",
    color: "hsl(210, 70%, 55%)",
  },
  {
    id: "ch4-o2",
    reactants: ["CH₄", "O₂"],
    products: ["CO₂", "H₂O"],
    equation: "CH₄ + 2O₂ → CO₂ + 2H₂O",
    type: "combustion",
    typeLabel: "Combustion",
    description: "Methane burns in oxygen - the reaction behind natural gas stoves.",
    observation: "Blue flame, heat, water vapor and CO₂ released",
    energy: "exothermic",
    color: "hsl(30, 90%, 55%)",
    minTemperature: 537,
  },
  {
    id: "k-h2o",
    reactants: ["K", "H₂O"],
    products: ["KOH", "H₂"],
    equation: "2K + 2H₂O → 2KOH + H₂",
    type: "single-replacement",
    typeLabel: "Single Replacement",
    description: "Potassium reacts explosively with water, even more vigorously than sodium.",
    observation: "Lilac/violet flame, rapid fizzing, may explode",
    energy: "exothermic",
    color: "hsl(280, 50%, 60%)",
    orderMatters: true,
    orderHint: "Add the metal to water, behind a safety screen.",
  },
  {
    id: "pb-ki",
    reactants: ["Pb(NO₃)₂", "KI"],
    products: ["PbI₂", "KNO₃"],
    equation: "Pb(NO₃)₂ + 2KI → PbI₂↓ + 2KNO₃",
    type: "double-replacement",
    typeLabel: "Double Replacement (Precipitation)",
    description: "Lead nitrate reacts with potassium iodide to form a striking yellow precipitate.",
    observation: "Brilliant yellow 'golden rain' precipitate",
    energy: "exothermic",
    color: "hsl(50, 90%, 60%)",
  },
  {
    id: "baking-soda-vinegar",
    reactants: ["NaHCO₃", "CH₃COOH"],
    products: ["CH₃COONa", "H₂O", "CO₂"],
    equation: "NaHCO₃ + CH₃COOH → CH₃COONa + H₂O + CO₂",
    type: "acid-base",
    typeLabel: "Acid-Base",
    description: "Baking soda + vinegar - the classic volcano reaction!",
    observation: "Vigorous fizzing and foaming, CO₂ gas released",
    energy: "endothermic",
    color: "hsl(0, 0%, 95%)",
  },
  {
    id: "ca-h2o",
    reactants: ["Ca", "H₂O"],
    products: ["Ca(OH)₂", "H₂"],
    equation: "Ca + 2H₂O → Ca(OH)₂ + H₂",
    type: "single-replacement",
    typeLabel: "Single Replacement",
    description: "Calcium reacts with water, less violently than sodium or potassium.",
    observation: "Gentle bubbling, milky solution (limewater)",
    energy: "exothermic",
    color: "hsl(0, 0%, 88%)",
  },
  {
    id: "h2o2-decomp",
    reactants: ["H₂O₂"],
    products: ["H₂O", "O₂"],
    equation: "2H₂O₂ → 2H₂O + O₂",
    type: "decomposition",
    typeLabel: "Decomposition",
    description: "Hydrogen peroxide decomposes into water and oxygen. Catalyzed by MnO₂ or yeast (elephant toothpaste!).",
    observation: "Bubbling, oxygen gas released, foaming with catalyst",
    energy: "exothermic",
    color: "hsl(200, 40%, 80%)",
  },
  {
    id: "thermite",
    reactants: ["Al", "Fe₂O₃"],
    products: ["Al₂O₃", "Fe"],
    equation: "2Al + Fe₂O₃ → Al₂O₃ + 2Fe",
    type: "single-replacement",
    typeLabel: "Single Replacement",
    description: "The thermite reaction: aluminum reduces iron(III) oxide, releasing enough heat to melt iron.",
    observation: "Blinding white sparks, molten iron drips, intense heat",
    energy: "exothermic",
    color: "hsl(20, 95%, 55%)",
    minTemperature: 800,
  },
  {
    id: "cu-fe-displacement",
    reactants: ["Fe", "CuSO₄"],
    products: ["FeSO₄", "Cu"],
    equation: "Fe + CuSO₄ → FeSO₄ + Cu",
    type: "single-replacement",
    typeLabel: "Single Replacement",
    description: "An iron nail in copper(II) sulfate displaces copper. The nail turns coppery as the blue solution fades to pale green.",
    observation: "Blue solution fades to pale green; reddish copper coats the iron",
    energy: "exothermic",
    color: "hsl(140, 40%, 60%)",
  },
  {
    id: "bacl2-na2so4",
    reactants: ["BaCl₂", "Na₂SO₄"],
    products: ["BaSO₄", "NaCl"],
    equation: "BaCl₂ + Na₂SO₄ → BaSO₄ + 2NaCl",
    type: "double-replacement",
    typeLabel: "Double Replacement",
    description: "A classic precipitation reaction - barium ions meet sulfate ions to form insoluble barium sulfate.",
    observation: "Dense white precipitate appears immediately",
    energy: "exothermic",
    color: "hsl(0, 0%, 96%)",
  },
  {
    id: "nh3-hcl",
    reactants: ["NH₃", "HCl"],
    products: ["NH₄Cl"],
    equation: "NH₃ + HCl → NH₄Cl",
    type: "synthesis",
    typeLabel: "Synthesis",
    description: "Ammonia gas meets hydrogen chloride gas - they combine in mid-air to form a thick white smoke of ammonium chloride.",
    observation: "Dense white smoke forms where the two vapors meet",
    energy: "exothermic",
    color: "hsl(0, 0%, 92%)",
  },
  {
    id: "sucrose-h2so4",
    reactants: ["C₁₂H₂₂O₁₁", "H₂SO₄"],
    products: ["C", "H₂O"],
    equation: "C₁₂H₂₂O₁₁ + H₂SO₄ → 12C + 11H₂O + H₂SO₄",
    type: "decomposition",
    typeLabel: "Dehydration",
    description: "Concentrated sulfuric acid dehydrates table sugar, leaving a black column of pure carbon - the classic 'black snake' demo.",
    observation: "Sugar turns yellow → brown → black; a steaming column of carbon rises from the beaker",
    energy: "exothermic",
    color: "hsl(0, 0%, 8%)",
    orderMatters: true,
    orderHint: "Always add the acid carefully to the sugar - never the reverse.",
  },
];

// Available chemicals for the lab shelf
export interface LabChemical {
  id: string;
  formula: string;
  name: string;
  state: "solid" | "liquid" | "gas" | "aqueous";
  color: string; // Visual color in container
  category: "element" | "compound" | "acid" | "base";
  /** Optional richer fill (CSS background) for the shelf swatch - e.g. metallic gradient. */
  swatchStyle?: string;
  /** One-line common use, shown in tooltip. */
  uses?: string;
  /** One-line safety note, shown prominently in tooltip when present. */
  safety?: string;
}

// Metallic gradient helper - soft highlight + base + shadow gives a polished feel.
const metallic = (h: number, s: number, l: number) =>
  `linear-gradient(135deg, hsl(${h}, ${s}%, ${Math.min(l + 18, 95)}%) 0%, hsl(${h}, ${s}%, ${l}%) 45%, hsl(${h}, ${s}%, ${Math.max(l - 18, 8)}%) 100%)`;

export const labChemicals: LabChemical[] = [
  { id: "na", formula: "Na", name: "Sodium", state: "solid", color: "hsl(45, 8%, 78%)", swatchStyle: metallic(45, 8, 72), category: "element",
    uses: "Street lamps, salt production", safety: "Reacts violently with water - store under oil." },
  { id: "k", formula: "K", name: "Potassium", state: "solid", color: "hsl(40, 6%, 72%)", swatchStyle: metallic(40, 6, 70), category: "element",
    uses: "Fertilizers, biological function", safety: "Even more reactive than sodium - ignites in water." },
  { id: "ca", formula: "Ca", name: "Calcium", state: "solid", color: "hsl(40, 5%, 82%)", swatchStyle: metallic(40, 5, 78), category: "element",
    uses: "Bones, cement, plaster" },
  { id: "mg", formula: "Mg", name: "Magnesium", state: "solid", color: "hsl(0, 0%, 80%)", swatchStyle: metallic(220, 4, 78), category: "element",
    uses: "Alloys, flares, fireworks", safety: "Burns with blinding white light - do not look directly." },
  { id: "fe", formula: "Fe", name: "Iron", state: "solid", color: "hsl(20, 8%, 45%)", swatchStyle: metallic(20, 10, 42), category: "element",
    uses: "Steel, structural construction" },
  { id: "zn", formula: "Zn", name: "Zinc", state: "solid", color: "hsl(220, 5%, 65%)", swatchStyle: metallic(220, 5, 65), category: "element",
    uses: "Galvanizing steel, batteries" },
  { id: "cu", formula: "Cu", name: "Copper", state: "solid", color: "hsl(25, 75%, 50%)", swatchStyle: metallic(25, 75, 48), category: "element",
    uses: "Electrical wiring, plumbing" },
  { id: "h2", formula: "H₂", name: "Hydrogen gas", state: "gas", color: "hsl(200, 30%, 90%)", category: "element",
    uses: "Rocket fuel, ammonia synthesis", safety: "Highly flammable - forms explosive mixtures with air." },
  { id: "o2", formula: "O₂", name: "Oxygen gas", state: "gas", color: "hsl(200, 50%, 85%)", category: "element",
    uses: "Respiration, combustion, steelmaking", safety: "Supports combustion - keep away from oils and sparks." },
  { id: "cl2", formula: "Cl₂", name: "Chlorine gas", state: "gas", color: "hsl(65, 65%, 60%)", category: "element",
    uses: "Water treatment, PVC production", safety: "Toxic - irritates lungs and eyes. Use a fume hood." },
  { id: "h2o", formula: "H₂O", name: "Water", state: "liquid", color: "hsl(200, 60%, 80%)", category: "compound",
    uses: "Universal solvent, life" },
  { id: "hcl", formula: "HCl", name: "Hydrochloric acid", state: "aqueous", color: "hsl(55, 25%, 90%)", category: "acid",
    uses: "Steel pickling, stomach acid", safety: "Strongly corrosive - wear gloves and goggles." },
  { id: "naoh", formula: "NaOH", name: "Sodium hydroxide", state: "aqueous", color: "hsl(200, 20%, 92%)", category: "base",
    uses: "Soap, drain cleaner, paper", safety: "Caustic - causes severe burns on skin contact." },
  { id: "agno3", formula: "AgNO₃", name: "Silver nitrate", state: "aqueous", color: "hsl(0, 0%, 95%)", category: "compound",
    uses: "Photography, antiseptic", safety: "Stains skin black; oxidiser - keep away from organics." },
  { id: "nacl", formula: "NaCl", name: "Sodium chloride", state: "solid", color: "hsl(0, 0%, 96%)", category: "compound",
    uses: "Table salt, food preservation" },
  { id: "caco3", formula: "CaCO₃", name: "Calcium carbonate", state: "solid", color: "hsl(0, 0%, 94%)", category: "compound",
    uses: "Limestone, chalk, antacids" },
  { id: "h2o2", formula: "H₂O₂", name: "Hydrogen peroxide", state: "liquid", color: "hsl(200, 30%, 92%)", category: "compound",
    uses: "Disinfectant, hair bleaching", safety: "Strong oxidiser at high concentration." },
  { id: "nahco3", formula: "NaHCO₃", name: "Baking soda", state: "solid", color: "hsl(0, 0%, 96%)", category: "compound",
    uses: "Baking, antacid, fire extinguishers" },
  { id: "ch3cooh", formula: "CH₃COOH", name: "Vinegar (acetic acid)", state: "liquid", color: "hsl(50, 30%, 88%)", category: "acid",
    uses: "Cooking, cleaning, preservative" },
  { id: "ch4", formula: "CH₄", name: "Methane", state: "gas", color: "hsl(0, 0%, 92%)", category: "compound",
    uses: "Natural gas, heating fuel", safety: "Extremely flammable - explosive in air at 5–15%." },
  { id: "pbno32", formula: "Pb(NO₃)₂", name: "Lead nitrate", state: "aqueous", color: "hsl(0, 0%, 92%)", category: "compound",
    uses: "Lab reagent, historical pigment", safety: "Toxic and an oxidiser - handle with care." },
  { id: "ki", formula: "KI", name: "Potassium iodide", state: "aqueous", color: "hsl(50, 25%, 92%)", category: "compound",
    uses: "Iodised salt, thyroid medicine" },
  { id: "al", formula: "Al", name: "Aluminum powder", state: "solid", color: "hsl(220, 4%, 78%)", swatchStyle: metallic(220, 4, 75), category: "element",
    uses: "Foil, alloys, thermite", safety: "Fine powder is flammable - keep ignition sources away." },
  { id: "fe2o3", formula: "Fe₂O₃", name: "Iron(III) oxide", state: "solid", color: "hsl(15, 65%, 38%)", category: "compound",
    uses: "Pigment (rust red), thermite oxidiser" },
  { id: "cuso4", formula: "CuSO₄", name: "Copper(II) sulfate", state: "aqueous", color: "hsl(210, 70%, 55%)", category: "compound",
    uses: "Fungicide, electroplating, classroom demos", safety: "Toxic if ingested - do not pour down drains." },
  { id: "bacl2", formula: "BaCl₂", name: "Barium chloride", state: "aqueous", color: "hsl(0, 0%, 94%)", category: "compound",
    uses: "Sulfate testing, fireworks (green)", safety: "Soluble barium salts are toxic - wear gloves." },
  { id: "na2so4", formula: "Na₂SO₄", name: "Sodium sulfate", state: "aqueous", color: "hsl(0, 0%, 95%)", category: "compound",
    uses: "Detergents, glass-making" },
  { id: "nh3", formula: "NH₃", name: "Ammonia gas", state: "gas", color: "hsl(60, 15%, 92%)", category: "compound",
    uses: "Fertilisers, cleaning products", safety: "Pungent and toxic - use a fume hood." },
  { id: "h2so4", formula: "H₂SO₄", name: "Sulfuric acid (conc.)", state: "liquid", color: "hsl(50, 20%, 88%)", category: "acid",
    uses: "Batteries, industrial chemistry", safety: "Severely corrosive and dehydrating - full PPE required." },
  { id: "sucrose", formula: "C₁₂H₂₂O₁₁", name: "Sucrose (table sugar)", state: "solid", color: "hsl(0, 0%, 96%)", category: "compound",
    uses: "Food, fermentation, dehydration demo" },
];

// Match chemicals in beaker to a known reaction
const normalize = (s: string) =>
  s.replace(/[₂₃₄]/g, (c) => ({ "₂": "2", "₃": "3", "₄": "4" }[c] || c)).toLowerCase();

/**
 * Find a reaction matching the chemicals currently in the beaker.
 *
 * @param chemicalFormulas formulas in the order they were added
 * @param temperature current beaker temperature in °C (default 25)
 */
export function findReaction(
  chemicalFormulas: string[],
  temperature: number = 25,
): Reaction | null {
  if (chemicalFormulas.length === 0) return null;

  for (const reaction of reactions) {
    const reactantSet = reaction.reactants.map(normalize).sort();
    const inputSet = chemicalFormulas.map(normalize).sort();

    if (reactantSet.length === inputSet.length && reactantSet.every((r, i) => r === inputSet[i])) {
      // Temperature gate
      const required = reaction.minTemperature ?? 25;
      if (temperature < required) continue;
      return reaction;
    }
  }
  return null;
}

export type NoReactionReason =
  | "needs-heat"
  | "spectator-ions"
  | "same-state-gases"
  | "noble-pair"
  | "incompatible"
  | "single-chemical"
  | "unknown";

export interface NoReactionExplanation {
  reason: NoReactionReason;
  message: string;
  /** When the reason is `needs-heat`, the minimum temperature in °C. */
  requiredTemperature?: number;
}

/**
 * Explain why no visible reaction occurs for this combination.
 * Falls through to a generic message when nothing specific applies.
 */
export function explainNoReaction(
  chemicalFormulas: string[],
  temperature: number = 25,
): NoReactionExplanation | null {
  if (chemicalFormulas.length === 0) return null;
  if (chemicalFormulas.length === 1) {
    return {
      reason: "single-chemical",
      message: "A single substance won't react on its own under normal conditions. Add another chemical to start an interaction.",
    };
  }

  const inputSet = chemicalFormulas.map(normalize).sort();

  // Check whether a known reaction exists at any temperature - if so, we likely just need heat.
  for (const reaction of reactions) {
    const reactantSet = reaction.reactants.map(normalize).sort();
    if (reactantSet.length === inputSet.length && reactantSet.every((r, i) => r === inputSet[i])) {
      const required = reaction.minTemperature ?? 25;
      if (temperature < required) {
        return {
          reason: "needs-heat",
          requiredTemperature: required,
          message: `These chemicals can react, but only above ~${required} °C. Turn up the hot plate to start the reaction.`,
        };
      }
    }
  }

  // Heuristic spectator-ion / unreactive pair detection
  const SPECTATOR_PAIRS = new Set([
    "nacl|h2o",
    "h2o|nacl",
    "nacl|naoh",
    "naoh|nacl",
  ]);
  const key = inputSet.join("|");
  if (SPECTATOR_PAIRS.has(key)) {
    return {
      reason: "spectator-ions",
      message: "Both substances dissolve into ions that don't combine to form a precipitate, gas, or weak electrolyte - they're spectator ions.",
    };
  }

  // Two inert gases in the same beaker
  const GASES = new Set(["h2", "o2", "cl2", "ch4"]);
  if (inputSet.length === 2 && inputSet.every((f) => GASES.has(f))) {
    return {
      reason: "same-state-gases",
      message: "These gases are mixed but won't react at room temperature without an ignition source. Try heating the beaker.",
    };
  }

  return {
    reason: "unknown",
    message: "No common reaction is known for this combination at this temperature. Try a different pairing or change the conditions.",
  };
}

/**
 * Return the minimum temperature (°C) required for any known reaction matching
 * these reactants, regardless of the current temperature. Returns `null` when
 * no reaction is defined for the combination.
 */
export function getRequiredTemperature(chemicalFormulas: string[]): number | null {
  if (chemicalFormulas.length === 0) return null;
  const inputSet = chemicalFormulas.map(normalize).sort();
  for (const reaction of reactions) {
    const reactantSet = reaction.reactants.map(normalize).sort();
    if (reactantSet.length === inputSet.length && reactantSet.every((r, i) => r === inputSet[i])) {
      return reaction.minTemperature ?? 25;
    }
  }
  return null;
}
